import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";
import { useAuth, API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { ChefHat, Heart, User as UserIcon, LogOut, Search, Star, ArrowLeft, Loader2, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import RecipeDisplay from "../../components/RecipeDisplay";

/* ============================================================================ *
 * Layout
 * ============================================================================ */

const NAV = [
  { to: "/cook", label: "Recipes", icon: ChefHat, end: true },
  { to: "/cook/favorites", label: "Favorites", icon: Heart },
  { to: "/cook/profile", label: "Profile", icon: UserIcon },
];

export function CookLayout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try { await logout(); } catch { /* ignore */ }
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar (mobile + desktop) */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-emerald-600 text-white flex items-center justify-center"><ChefHat size={16} /></div>
            <span className="font-extrabold text-gray-900 text-sm">Cook</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500 hidden sm:inline">{user?.email}</span>
            <Button size="sm" variant="outline" onClick={handleLogout} className="text-[10px] h-7" data-testid="cook-logout"><LogOut size={11} className="mr-1" />Logout</Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-4 sm:p-6 pb-24">{children}</main>

      {/* Bottom nav for mobile */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 z-30 md:hidden">
        <div className="grid grid-cols-3 max-w-md mx-auto">
          {NAV.map((n) => {
            const active = n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to);
            const Icon = n.icon;
            return (
              <Link key={n.to} to={n.to} className={`py-2.5 flex flex-col items-center gap-0.5 text-[10px] ${active ? "text-emerald-700 font-bold" : "text-gray-500"}`} data-testid={`cook-nav-${n.label.toLowerCase()}`}>
                <Icon size={16} />
                {n.label}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Desktop side rail */}
      <aside className="hidden md:flex fixed top-20 left-4 flex-col gap-1 z-20 bg-white border border-gray-100 rounded-2xl p-2 shadow-sm">
        {NAV.map((n) => {
          const active = n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to);
          const Icon = n.icon;
          return (
            <Link key={n.to} to={n.to} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap ${active ? "bg-emerald-50 text-emerald-700" : "text-gray-600 hover:bg-gray-50"}`}>
              <Icon size={14} />
              {n.label}
            </Link>
          );
        })}
      </aside>
    </div>
  );
}

/* ============================================================================ *
 * Recipes (browse)
 * ============================================================================ */

export function CookRecipes() {
  const loc = useLocation();
  const restored = loc.state?.restoreFilters;
  const [recipes, setRecipes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [kitchens, setKitchens] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [q, setQ] = useState(restored?.q || "");
  const [activeCat, setActiveCat] = useState(restored?.activeCat || "all");
  const [activeTag, setActiveTag] = useState(restored?.activeTag || "all");
  const [tagQuery, setTagQuery] = useState("");
  const [tagFocused, setTagFocused] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadDeps = async () => {
    const [c, t, k, fav] = await Promise.all([
      fetch(`${API}/recipe-categories`, { credentials: "include" }).then((r) => r.ok ? r.json() : []),
      fetch(`${API}/recipe-tags`, { credentials: "include" }).then((r) => r.ok ? r.json() : []),
      fetch(`${API}/kitchens`, { credentials: "include" }).then((r) => r.ok ? r.json() : []),
      fetch(`${API}/me/recipe-favorites`, { credentials: "include" }).then((r) => r.ok ? r.json() : []),
    ]);
    setCategories(c); setTags(t); setKitchens(k); setFavorites(fav.map((x) => x.recipe_id));
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (activeCat !== "all") params.set("recipe_category_id", activeCat);
      if (activeTag !== "all") params.set("tag_id", activeTag);
      const r = await fetch(`${API}/recipes?${params.toString()}`, { credentials: "include" });
      setRecipes(r.ok ? await r.json() : []);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadDeps(); }, []);
  useEffect(() => {
    const id = setTimeout(load, 250);
    return () => clearTimeout(id);
    // eslint-disable-next-line
  }, [q, activeCat, activeTag]);

  const tagById = useMemo(() => Object.fromEntries(tags.map((t) => [t.recipe_tag_id, t])), [tags]);
  const catById = useMemo(() => Object.fromEntries(categories.map((c) => [c.recipe_category_id, c])), [categories]);
  const kitchenById = useMemo(() => Object.fromEntries(kitchens.map((k) => [k.kitchen_id, k])), [kitchens]);

  const toggleFav = async (rid) => {
    const isFav = favorites.includes(rid);
    setFavorites((p) => isFav ? p.filter((x) => x !== rid) : [...p, rid]);
    try {
      const r = await fetch(`${API}/me/recipe-favorites/${rid}`, { method: isFav ? "DELETE" : "POST", credentials: "include" });
      if (!r.ok) throw new Error("Failed");
    } catch {
      // revert
      setFavorites((p) => isFav ? [...p, rid] : p.filter((x) => x !== rid));
      toast.error("Couldn't update favorite");
    }
  };

  return (
    <CookLayout>
      <div className="space-y-4 md:ml-32" data-testid="cook-recipes">
        <div className="flex items-center gap-3 mb-1">
          <h1 className="text-lg sm:text-xl font-extrabold text-gray-900">Recipes</h1>
        </div>

        {/* Search */}
        <div className="bg-white rounded-2xl border border-gray-100 p-3 sticky top-16 z-20">
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search recipes…" className="pl-9 text-sm" data-testid="cook-search" />
          </div>
          {/* Category chips */}
          <div className="flex items-center gap-2 overflow-x-auto pt-2 pb-1">
            <button
              type="button"
              onClick={() => setActiveCat("all")}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${activeCat === "all" ? "bg-emerald-600 text-white" : "bg-white border border-gray-200 text-gray-600"}`}
              data-testid="cook-cat-all"
            >All</button>
            {categories.map((c) => (
              <button
                key={c.recipe_category_id}
                type="button"
                onClick={() => setActiveCat(c.recipe_category_id)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${activeCat === c.recipe_category_id ? "bg-emerald-600 text-white" : "bg-white border border-gray-200 text-gray-600"}`}
                data-testid={`cook-cat-${c.recipe_category_id}`}
              >{c.name}</button>
            ))}
          </div>
          {/* Tag live-search — second filter row */}
          {tags.length > 0 && (
            <div className="pt-1.5" data-testid="cook-tag-search-row">
              {activeTag !== "all" ? (
                (() => {
                  const t = tagById[activeTag];
                  if (!t) return null;
                  return (
                    <button
                      type="button"
                      onClick={() => { setActiveTag("all"); setTagQuery(""); }}
                      className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1"
                      style={{ backgroundColor: t.color_hex, color: "#fff" }}
                      data-testid={`cook-tag-active-${t.recipe_tag_id}`}
                      title="Clear tag filter"
                    >
                      {t.name}
                      <XIcon size={10} />
                    </button>
                  );
                })()
              ) : (
                <div className="relative">
                  <Input
                    value={tagQuery}
                    onChange={(e) => setTagQuery(e.target.value)}
                    onFocus={() => setTagFocused(true)}
                    onBlur={() => setTimeout(() => setTagFocused(false), 150)}
                    placeholder="Filter by tag…"
                    className="text-[11px] h-8"
                    data-testid="cook-tag-search-input"
                  />
                  {tagFocused && tagQuery.trim() && (() => {
                    const needle = tagQuery.trim().toLowerCase();
                    const matches = tags.filter((t) => t.name.toLowerCase().includes(needle)).slice(0, 8);
                    if (matches.length === 0) {
                      return (
                        <div className="absolute z-40 mt-1 w-full bg-white border border-gray-100 rounded-lg shadow-md p-2 text-[10px] text-gray-400" data-testid="cook-tag-search-empty">
                          No matching tags
                        </div>
                      );
                    }
                    return (
                      <ul className="absolute z-40 mt-1 w-full bg-white border border-gray-100 rounded-lg shadow-md max-h-48 overflow-y-auto" data-testid="cook-tag-search-results">
                        {matches.map((t) => (
                          <li key={t.recipe_tag_id}>
                            <button
                              type="button"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => { setActiveTag(t.recipe_tag_id); setTagQuery(""); setTagFocused(false); }}
                              className="w-full text-left px-3 py-2 text-xs hover:bg-emerald-50 flex items-center gap-2"
                              data-testid={`cook-tag-search-option-${t.recipe_tag_id}`}
                            >
                              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: t.color_hex }} />
                              <span className="font-semibold text-gray-700">{t.name}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    );
                  })()}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Cards */}
        {loading ? (
          <div className="p-8 text-center text-gray-400"><Loader2 className="animate-spin inline" size={16} /></div>
        ) : recipes.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">No recipes match. Try a different category or clear the search.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {recipes.map((r) => (
              <Link key={r.recipe_id} to={`/cook/recipes/${r.recipe_id}`} state={{ from: "/cook", filters: { q, activeCat, activeTag } }} className="bg-white border border-gray-100 rounded-2xl p-4 hover:border-emerald-200 transition-colors" data-testid={`cook-recipe-card-${r.recipe_id}`}>
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-bold text-sm text-gray-900 break-words flex-1">{r.name}</h3>
                  <button onClick={(e) => { e.preventDefault(); toggleFav(r.recipe_id); }} className="flex-shrink-0" data-testid={`cook-fav-${r.recipe_id}`}>
                    <Star size={16} className={favorites.includes(r.recipe_id) ? "fill-amber-400 text-amber-400" : "text-gray-300"} />
                  </button>
                </div>
                <p className="text-[11px] text-gray-500 mt-0.5">{catById[r.recipe_category_id]?.name || "—"}</p>
                {(r.tag_ids?.length > 0 || r.kitchen_ids?.length > 0) && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(r.tag_ids || []).map((tid) => {
                      const t = tagById[tid]; if (!t) return null;
                      return <span key={tid} className="text-[9px] font-semibold px-1.5 py-0.5 rounded" style={{ backgroundColor: `${t.color_hex}20`, color: t.color_hex }}>{t.name}</span>;
                    })}
                    {(r.kitchen_ids || []).map((kid) => {
                      const k = kitchenById[kid]; if (!k) return null;
                      return <span key={kid} className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">{k.kitchen_code}</span>;
                    })}
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    </CookLayout>
  );
}

/* ============================================================================ *
 * Recipe Detail
 * ============================================================================ */

export function CookRecipeDetail() {
  const { recipe_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [recipe, setRecipe] = useState(null);
  const [tags, setTags] = useState([]);
  const [categories, setCategories] = useState([]);
  const [kitchens, setKitchens] = useState([]);
  const [isFav, setIsFav] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [rec, t, c, k, fav] = await Promise.all([
          fetch(`${API}/recipes/${recipe_id}`, { credentials: "include" }).then((r) => r.ok ? r.json() : null),
          fetch(`${API}/recipe-tags`, { credentials: "include" }).then((r) => r.ok ? r.json() : []),
          fetch(`${API}/recipe-categories`, { credentials: "include" }).then((r) => r.ok ? r.json() : []),
          fetch(`${API}/kitchens`, { credentials: "include" }).then((r) => r.ok ? r.json() : []),
          fetch(`${API}/me/recipe-favorites`, { credentials: "include" }).then((r) => r.ok ? r.json() : []),
        ]);
        setRecipe(rec); setTags(t); setCategories(c); setKitchens(k);
        setIsFav(fav.some((x) => x.recipe_id === recipe_id));
      } finally { setLoading(false); }
    })();
  }, [recipe_id]);

  const tagById = Object.fromEntries(tags.map((t) => [t.recipe_tag_id, t]));
  const catById = Object.fromEntries(categories.map((c) => [c.recipe_category_id, c]));
  const kitchenById = Object.fromEntries(kitchens.map((k) => [k.kitchen_id, k]));

  const toggleFav = async () => {
    const next = !isFav; setIsFav(next);
    try { await fetch(`${API}/me/recipe-favorites/${recipe_id}`, { method: next ? "POST" : "DELETE", credentials: "include" }); }
    catch { setIsFav(!next); toast.error("Couldn't update favorite"); }
  };

  return (
    <CookLayout>
      <div className="md:ml-32 space-y-4" data-testid="cook-recipe-detail">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            const back = location.state?.from;
            const restoreFilters = location.state?.filters;
            const dest = back || "/cook";
            navigate(dest, { replace: true, state: restoreFilters ? { restoreFilters } : undefined });
          }}
          className="text-xs"
          data-testid="cook-back"
        >
          <ArrowLeft size={12} className="mr-1" /> Back
        </Button>
        {loading ? (
          <div className="p-8 text-center text-gray-400"><Loader2 className="animate-spin inline" size={16} /></div>
        ) : !recipe ? (
          <div className="p-8 text-center text-gray-400 text-sm">Recipe not available.</div>
        ) : (
          <RecipeDisplay
            recipe={recipe}
            category={catById[recipe.recipe_category_id]}
            tagsById={tagById}
            kitchensById={kitchenById}
            linkBasePath="/cook/recipes"
            currentPath={location.pathname}
            rightAction={
              <Button onClick={toggleFav} variant="outline" className="text-xs" data-testid="cook-fav-toggle">
                <Star size={13} className={`mr-1 ${isFav ? "fill-amber-400 text-amber-400" : ""}`} />
                {isFav ? "Favorited" : "Favorite"}
              </Button>
            }
          />
        )}
      </div>
    </CookLayout>
  );
}

function KeyVal({ label, value }) {
  return (
    <div className="bg-gray-50 rounded-lg p-2.5">
      <p className="text-[10px] uppercase text-gray-400 font-semibold">{label}</p>
      <p className="text-sm font-bold text-gray-900 mt-0.5">{value}</p>
    </div>
  );
}

/* ============================================================================ *
 * Favorites
 * ============================================================================ */

export function CookFavorites() {
  const [favs, setFavs] = useState([]);
  const [tags, setTags] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const [f, t, c] = await Promise.all([
        fetch(`${API}/me/recipe-favorites`, { credentials: "include" }).then((r) => r.ok ? r.json() : []),
        fetch(`${API}/recipe-tags`, { credentials: "include" }).then((r) => r.ok ? r.json() : []),
        fetch(`${API}/recipe-categories`, { credentials: "include" }).then((r) => r.ok ? r.json() : []),
      ]);
      setFavs(f); setTags(t); setCategories(c);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const tagById = Object.fromEntries(tags.map((t) => [t.recipe_tag_id, t]));
  const catById = Object.fromEntries(categories.map((c) => [c.recipe_category_id, c]));

  const remove = async (rid) => {
    setFavs((p) => p.filter((x) => x.recipe_id !== rid));
    try { await fetch(`${API}/me/recipe-favorites/${rid}`, { method: "DELETE", credentials: "include" }); }
    catch { load(); toast.error("Couldn't remove"); }
  };

  return (
    <CookLayout>
      <div className="md:ml-32 space-y-4" data-testid="cook-favorites">
        <h1 className="text-lg sm:text-xl font-extrabold text-gray-900">Favorites</h1>
        {loading ? <Loader2 className="animate-spin" size={16} /> : favs.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm bg-white rounded-2xl border border-gray-100">No favorites yet. Star a recipe from the list to pin it here.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {favs.map((r) => (
              <Link key={r.recipe_id} to={`/cook/recipes/${r.recipe_id}`} className="bg-white border border-gray-100 rounded-2xl p-4 hover:border-emerald-200" data-testid={`cook-fav-card-${r.recipe_id}`}>
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-bold text-sm text-gray-900">{r.name}</h3>
                  <button onClick={(e) => { e.preventDefault(); remove(r.recipe_id); }}><Star size={16} className="fill-amber-400 text-amber-400" /></button>
                </div>
                <p className="text-[11px] text-gray-500 mt-0.5">{catById[r.recipe_category_id]?.name || "—"}</p>
                {r.tag_ids?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {r.tag_ids.map((tid) => {
                      const t = tagById[tid]; if (!t) return null;
                      return <span key={tid} className="text-[9px] font-semibold px-1.5 py-0.5 rounded" style={{ backgroundColor: `${t.color_hex}20`, color: t.color_hex }}>{t.name}</span>;
                    })}
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    </CookLayout>
  );
}

/* ============================================================================ *
 * Profile
 * ============================================================================ */

export function CookProfile() {
  const { user } = useAuth();
  return (
    <CookLayout>
      <div className="md:ml-32 space-y-4" data-testid="cook-profile">
        <h1 className="text-lg sm:text-xl font-extrabold text-gray-900">Profile</h1>
        <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-3">
          <KeyVal label="Name" value={user?.name || "—"} />
          <KeyVal label="Email" value={user?.email || "—"} />
          <KeyVal label="Role" value={user?.role || "—"} />
        </div>
      </div>
    </CookLayout>
  );
}
